import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { driver, DriveStep, Config } from 'driver.js';
import { TranslateService } from '@ngx-translate/core';

/**
 * A single logical part of a multi-page tour.
 * - `steps`: the tour steps for this page.
 * - `route` (optional): the route to navigate to BEFORE showing these steps.
 *    Can be a string (e.g. '/home') or Angular commands array (e.g. ['/home','view-questions', ...]).
 * - `waitForSelector` (optional): after navigating, wait until this element exists in the DOM
 *    before starting the segment's steps.
 */
interface TourSegment {
  steps: DriveStep[];
  route?: string | any[];
  waitForSelector?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private driverObj = driver({
    showProgress: true,
    showButtons: ['next', 'previous', 'close'],
    steps: []
  });

  // Tracks the number of (visible) steps per segment for a cross-page tour,
  // so the progress counter can show a combined "Step X of TOTAL".
  private segmentStepCounts: number[] = [];
  private combinedTotalSteps = 0;

  constructor(private translate: TranslateService, private router: Router) { }

  /**
   * Start a custom tour with provided steps
   * @param steps Array of tour steps
   * @param config Optional driver configuration
   */
  startTour(steps: DriveStep[], config?: Partial<Config>, startAtIndex: number = 0): void {
    const defaultConfig: Config = {
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      steps: steps,
      nextBtnText: (this.translate.instant('tour.next') !== 'tour.next') ? this.translate.instant('tour.next') : 'Next',
      prevBtnText: (this.translate.instant('tour.previous') !== 'tour.previous') ? this.translate.instant('tour.previous') : 'Previous',
      doneBtnText: (this.translate.instant('tour.done') !== 'tour.done') ? this.translate.instant('tour.done') : 'Finish',
      progressText: (this.translate.instant('tour.progress') !== 'tour.progress') ? this.translate.instant('tour.progress') : 'Step {{current}} of {{total}}',
      onDestroyStarted: () => {
        this.driverObj.destroy();
      },
      onHighlightStarted: (element) => {
        // Add custom styling or behavior when element is highlighted
         if (element instanceof HTMLElement) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }
      },
      onPopoverRender: (popover, { config, state }) => {
        // Customize popover appearance
        const popoverElement = popover.wrapper;
        popoverElement.style.borderRadius = '8px';
        popoverElement.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';
      }
    };

    // Merge custom config with default config
    const finalConfig = { ...defaultConfig, ...config };
    
    this.driverObj = driver(finalConfig);
    this.driverObj.drive(startAtIndex);
  }

  startAckTour(steps: DriveStep[], config?: Partial<Config>): void {
    const defaultConfig: Config = {
      showProgress: false,
      showButtons: ['close'], // Only show close button
      allowClose: true,
      steps: steps,
      doneBtnText: (this.translate.instant('tour.Done') !== 'tour.Done') ? this.translate.instant('tour.Done') : 'Done',
      nextBtnText: '', // Empty to hide
      prevBtnText: '', // Empty to hide
      onDestroyStarted: () => {
        this.driverObj.destroy();
      },
      onHighlightStarted: (element) => {
        // Add custom styling or behavior when element is highlighted
      },
      onPopoverRender: (popover, { config, state }) => {
        // Customize popover appearance
        const popoverElement = popover.wrapper;
        popoverElement.style.borderRadius = '8px';
        popoverElement.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';

        // Force hide unwanted elements
        this.hideUnwantedElements(popoverElement);
      }
    };

    // Merge custom config with default config
    const finalConfig = { ...defaultConfig, ...config };

    this.driverObj = driver(finalConfig);
    this.driverObj.drive();
  }

  private hideUnwantedElements(popoverElement: HTMLElement): void {
    // Hide progress text
    const progressElements = popoverElement.querySelectorAll('[class*="progress"], [class*="step"]');
    progressElements.forEach(el => {
      if (el.textContent?.includes('of')) {
        (el as HTMLElement).style.display = 'none';
      }
    });

    // Hide previous button
    const prevButtons = popoverElement.querySelectorAll('button');
    prevButtons.forEach(btn => {
      if (btn.textContent?.toLowerCase().includes('previous') ||
        btn.textContent?.toLowerCase().includes('prev') ||
        btn.classList.contains('driver-prev-btn')) {
        (btn as HTMLElement).style.display = 'none';
      }
    });
  }

 private getCurrentAffiliate(): string {  
        return localStorage.getItem('Affiliate') || '';  
  }

  /**
   * Start a tour for dashboard page
   */
  startDashboardTour(): void {
    this.startTour(this.getDashboardSteps());
  }

  /**
   * Return the dashboard tour steps (without starting the tour).
   * Useful for reusing/combining with other tours.
   */
  private getDashboardSteps(): DriveStep[] {
    return [
      {
        element: '[data-tour="dashboard-welcome"]',
        popover: {
          title: this.translate.instant('tour.dashboard.welcome.title'),
          description: this.translate.instant('tour.dashboard.welcome.description'),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '[data-tour="affiliate-selector"]',
        popover: {
          title: this.translate.instant('tour.dashboard.affiliate.title'),
          description: this.translate.instant('tour.dashboard.affiliate.description'),
          side: 'left',
          align: 'start'
        }
      },
      {
        element: '[data-tour="language-selector"]',
        popover: {
          title: this.translate.instant('tour.dashboard.language.title'),
          description: this.translate.instant('tour.dashboard.language.description'),
          side: 'left',
          align: 'start'
        }
      },
      {
        element: '[data-tour="user-profile"]',
        popover: {
          title: this.translate.instant('tour.dashboard.account.title'),
          description: this.translate.instant('tour.dashboard.account.description'),
          side: 'left',
          align: 'start'
        }
      },
      {
        element: '[data-tour="navigation-menu"]',
        popover: {
          title: this.translate.instant('tour.dashboard.navigation.title'),
          description: this.translate.instant('tour.dashboard.navigation.description'),
          side: 'bottom',
          align: 'start'
        }
      },
      {
        element: '[data-tour="category-section"]',
        popover: {
          title: this.translate.instant('tour.dashboard.category.title'),
          description: this.translate.instant('tour.dashboard.category.description'),
          side: 'top',
          align: 'center'
        }
      },
      {
        element: '[data-tour="submitted-disclosures-table"]',
        popover: {
          title: this.translate.instant('tour.dashboard.submitted.title'),
          description: this.translate.instant('tour.dashboard.submitted.description'),
          side: 'top',
          align: 'center'
        }
      }

    ];
  }

  /**
   * Return the home view-questions tour steps (without starting the tour).
   * Useful for reusing/combining with other tours.
   */
  private getHomeViewQuestionsSteps(): DriveStep[] {
    const currentAffiliate = this.getCurrentAffiliate();
    const allowedAffiliates = ['TMN', 'TFS'];
    return [
      {
        element: '[data-tour="disclosure-summary-home"]',
        popover: {
          title: this.translate.instant('tour.home.summary.title'),
          description: this.translate.instant('tour.home.summary.description'),
          side: 'bottom',
          align: 'center'
        }
      },
      // Conditionally add "On Behalf of" step only for TMN and TFS
      ...(allowedAffiliates.includes(currentAffiliate) ? [{
        element: '[data-tour="on-behalf-of"]',
        popover: {
          title: this.translate.instant('tour.home.onBehalf.title'),
          description: this.translate.instant('tour.home.onBehalf.description'),
          side: 'bottom' as const,
          align: 'center' as const
        }
      }] as DriveStep[] : [] as DriveStep[]),
      {
        element: '[data-tour="submitter-info-card"]',
        popover: {
          title: this.translate.instant('tour.home.submitterInfo.title'),
          description: this.translate.instant('tour.home.submitterInfo.description'),
          side: 'bottom',
          align: 'center'
        }
      },
      {
        element: '[data-tour="List-of-questions"]',
        popover: {
          title: this.translate.instant('tour.home.questions.title'),
          description: this.translate.instant('tour.home.questions.description'),
          side: 'bottom',
          align: 'center'
        }
      },
      {
        element: '[data-tour="discussion-box-input"]',
        popover: {
          title: this.translate.instant('tour.home.discussion.title'),
          description: this.translate.instant('tour.home.discussion.description'),
          side: 'bottom',
          align: 'center'
        }
      },
      {
        element: '[data-tour="attachments-section"]',
        popover: {
          title: this.translate.instant('tour.home.attachments.title'),
          description: this.translate.instant('tour.home.attachments.description'),
          side: 'bottom',
          align: 'center'
        }
      },
      {
        element: '[data-tour="draft-submit-buttons"]',
        popover: {
          title: this.translate.instant('tour.home.draftSubmit.title'),
          description: this.translate.instant('tour.home.draftSubmit.description'),
          side: 'top',
          align: 'center'
        }
      },
      {
        element: '[data-tour="tour-button"]',
        popover: {
          title: this.translate.instant('tour.home.help.title'),
          description: this.translate.instant('tour.home.help.description'),
          side: 'bottom',
          align: 'center'
        }
      }
    ];
  }

  /**
   * Start a single combined tour that includes both the Dashboard steps and
   * the Home (view questions) steps. Only steps whose elements are currently
   * present in the DOM will be shown, so this can be safely called on either page.
   */
  startCombinedDashboardHomeTour(): void {
    // De-duplicate steps that share the same element selector (e.g. tour-button)
    const combinedSteps = [
      ...this.getDashboardSteps(),
      ...this.getHomeViewQuestionsSteps()
    ];

    const seen = new Set<string>();
    const filteredSteps = combinedSteps.filter((step: any) => {
      const selector = step.element as string;
      if (seen.has(selector)) {
        return false;
      }
      if (!document.querySelector(selector)) {
        return false;
      }
      seen.add(selector);
      return true;
    });

    this.startTour(filteredSteps);
  }

  /**
   * Start a combined Dashboard -> Home (view questions) tour that spans TWO routes.
   * It shows the dashboard steps first, then when the user clicks "Next" on the last
   * dashboard step it navigates to the provided view-questions route, waits for the
   * page to render, and continues with the home steps.
   *
   * @param viewQuestionsRoute The route to the view-questions page. Because that route
   *   requires params, pass the full commands array, e.g.
   *   ['/home', 'view-questions', category, affiliate, id]
   */
  startCombinedDashboardHomeTourCrossPage(viewQuestionsRoute: string | any[]): void {
    const segments: TourSegment[] = [
      {
        // Dashboard page (already on it when the tour starts).
        // route/waitForSelector are used only when navigating BACK to this segment.
        route: '/home',
        waitForSelector: '[data-tour="dashboard-welcome"]',
        steps: this.getDashboardSteps()
      },
      {
        // Navigate to the disclosure's view-questions page, then continue
        route: viewQuestionsRoute,
        waitForSelector: '[data-tour="disclosure-summary-home"]',
        steps: this.getHomeViewQuestionsSteps()
      }
    ];

    // Initialise combined step counting. The first segment is on-screen now so we
    // can count its visible steps; later segments aren't rendered yet, so we use
    // their full step length as an estimate (refined when each segment runs).
    this.segmentStepCounts = segments.map((seg, i) => {
      if (i === 0) {
        return seg.steps.filter(
          (step: any) => !!document.querySelector(step.element as string)
        ).length;
      }
      return seg.steps.length;
    });
    this.combinedTotalSteps = this.segmentStepCounts.reduce((a, b) => a + b, 0);

    // Mark the tour as in progress for the WHOLE duration (including the page
    // navigation gap between segments) so the page's loading spinner can be
    // suppressed via CSS while the tour runs.
    this.setTourInProgress(true);

    this.runSegmentedTour(segments, 0);
  }

  /**
   * Toggle a body-level class used to hide the app's full-screen loading spinner
   * while a (cross-page) tour is running. This covers the brief window during
   * route navigation where no tour popover is on screen yet.
   */
  private setTourInProgress(active: boolean): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }
    document.body.classList.toggle('tour-in-progress', active);
  }

  /**
   * Generic engine that runs a tour across multiple pages/routes.
   * Handles navigation between segments and waiting for the next page to render.
   */
  private runSegmentedTour(segments: TourSegment[], index: number, startAtIndex: number = 0): void {
    const segment = segments[index];

    // Only keep steps whose elements are currently present in the DOM.
    const steps = segment.steps.filter(
      (step: any) => !!document.querySelector(step.element as string)
    );

    const isFirstSegment = index === 0;
    const isLastSegment = index === segments.length - 1;

    // If this segment has no visible steps, skip straight to the next one.
    if (steps.length === 0) {
      if (!isLastSegment) {
        this.goToSegment(segments, index + 1);
      } else {
        // Nothing left to show: make sure the spinner-suppressing class is cleared.
        this.setTourInProgress(false);
      }
      return;
    }

    // Clamp the requested start index (used when navigating back to a segment).
    const initialIndex = Math.min(Math.max(startAtIndex, 0), steps.length - 1);

    // Refine the combined step counting now that we know this segment's real
    // (DOM-filtered) step count, then work out this segment's starting offset.
    if (this.segmentStepCounts.length === segments.length) {
      this.segmentStepCounts[index] = steps.length;
      this.combinedTotalSteps = this.segmentStepCounts.reduce((a, b) => a + b, 0);
    }
    const stepOffset = this.segmentStepCounts
      .slice(0, index)
      .reduce((a, b) => a + b, 0);
    const useCombinedProgress = this.combinedTotalSteps > 0 && segments.length > 1;

    this.startTour(steps, {
      // Ensure the spinner-suppressing body class is cleared if the user closes
      // the tour  at any point.
      onDestroyStarted: () => {
        this.hideTransitionOverlay();
        this.setTourInProgress(false);
        this.driverObj.destroy();
      },
      // Take over the "Next" click so we can hand off to the next page on the last step.
      onNextClick: (_element, _step, opts) => {
        const activeIndex = opts.state.activeIndex ?? 0;
        const isLastStep = activeIndex >= steps.length - 1;

        if (isLastStep && !isLastSegment) {
          this.driverObj.destroy();
          this.goToSegment(segments, index + 1);
        } else {
          // Natural end of the whole tour: clear the spinner-suppressing class.
          if (isLastStep && isLastSegment) {
            this.setTourInProgress(false);
          }
          this.driverObj.moveNext();
        }
      },
      // Take over the "Previous" click so we can go back to the previous page
      // when the user is on the first step of a later segment.
      onPrevClick: (_element, _step, opts) => {
        const activeIndex = opts.state.activeIndex ?? 0;
        const isFirstStep = activeIndex <= 0;

        if (isFirstStep && !isFirstSegment) {
          this.driverObj.destroy();
          // Go back and land on the LAST step of the previous segment.
          this.goToSegment(segments, index - 1, 'last');
        } else {
          this.driverObj.movePrevious();
        }
      },
      // Show a COMBINED progress count across all segments/pages
      // (e.g. "Step 3 of 13") instead of restarting on each page.
      onPopoverRender: (popover, opts) => {
        const popoverElement = popover.wrapper;
        popoverElement.style.borderRadius = '8px';
        popoverElement.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';

        const activeIndex = opts.state.activeIndex ?? 0;

        if (useCombinedProgress && popover.progress) {
          const current = stepOffset + activeIndex + 1;
          popover.progress.innerText = this.buildProgressText(current, this.combinedTotalSteps);
        }

        // Driver.js disables the Previous button on the first step of THIS instance.
        // For a cross-page tour, the first step of a later segment should still be
        // able to go back to the previous page, so re-enable it.
        if (popover.previousButton && activeIndex <= 0 && !isFirstSegment) {
          popover.previousButton.disabled = false;
          popover.previousButton.classList.remove('driver-popover-btn-disabled');
        }

        // Driver.js shows the "Finish/Done" label on the last step of THIS instance.
        // For a cross-page tour, the last step of a non-final segment should still say
        // "Next" (it hands off to the next page), so relabel the button.
        const isLastStepOfSegment = activeIndex >= steps.length - 1;
        if (popover.nextButton && isLastStepOfSegment && !isLastSegment) {
          popover.nextButton.innerText =
            (this.translate.instant('tour.next') !== 'tour.next')
              ? this.translate.instant('tour.next')
              : 'Next';
        }
      }
    }, initialIndex);
  }

  /**
   * Build the progress label ("Step X of Y"), honouring the translation if present.
   */
  private buildProgressText(current: number, total: number): string {
    const template = (this.translate.instant('tour.progress') !== 'tour.progress')
      ? this.translate.instant('tour.progress')
      : 'Step {{current}} of {{total}}';
    return template
      .replace('{{current}}', String(current))
      .replace('{{total}}', String(total));
  }

  /**
   * Navigate to a segment's route (if any), wait for its anchor element,
   * then run that segment's steps. `startAt` controls which step to begin on:
   * a number, or 'last' to start on the segment's final step.
   */
  private goToSegment(segments: TourSegment[], index: number, startAt: number | 'last' = 0): void {
    const segment = segments[index];
    const proceed = () => {
      // Tear down the temporary bridging overlay (if any) right before the real
      // segment starts, so there's a seamless handover with no blank gap.
      this.hideTransitionOverlay();
      const stepIndex = startAt === 'last' ? Number.MAX_SAFE_INTEGER : startAt;
      this.runSegmentedTour(segments, index, stepIndex);
    };

    if (segment.route) {
      // Show a bridging overlay immediately so the destination page's blank/loading
      // state is covered until the target step's element is ready.
      this.showTransitionOverlay();
      const commands = Array.isArray(segment.route) ? segment.route : [segment.route];
      this.router.navigate(commands).then(() => {
        this.waitForElement(segment.waitForSelector, proceed);
      });
    } else {
      proceed();
    }
  }

  // Holds the temporary driver instance used to cover page transitions.
  private transitionDriverObj: ReturnType<typeof driver> | null = null;

  /**
   * Show a lightweight full-screen overlay with a "loading" message while the
   * next page navigates/renders. This prevents the user from briefly seeing a
   * blank page between two tour segments.
   */
  private showTransitionOverlay(): void {
    // Avoid stacking multiple overlays.
    this.hideTransitionOverlay();

    const loadingTitle =
      (this.translate.instant('tour.loadingTitle') !== 'tour.loadingTitle')
        ? this.translate.instant('tour.loadingTitle')
        : 'Loading Next Step';

    const loadingText =
      (this.translate.instant('tour.loading') !== 'tour.loading')
        ? this.translate.instant('tour.loading')
        : 'Preparing the next steps. This will only take a second…';

    this.transitionDriverObj = driver({
      showProgress: false,
      showButtons: [],
      allowClose: false,
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      popoverClass: 'tour-loading-popover',
      steps: [
        {
          popover: {
            title: loadingTitle,
            description: loadingText,
            side: 'over',
            align: 'center'
          }
        }
      ],
      onPopoverRender: (popover) => {
        const popoverElement = popover.wrapper;
        popoverElement.style.borderRadius = '8px';
        popoverElement.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.15)';

        // The loading overlay is purely informational — hide the footer/nav
        // buttons (Previous / Next / Done) and the close (X) button.
        if (popover.previousButton) {
          popover.previousButton.style.display = 'none';
        }
        if (popover.nextButton) {
          popover.nextButton.style.display = 'none';
        }
        if (popover.footer) {
          popover.footer.style.display = 'none';
        }
        if (popover.closeButton) {
          popover.closeButton.style.display = 'none';
        }
      }
    });
    this.transitionDriverObj.drive();
  }

  /**
   * Destroy the bridging overlay shown during a page transition (if present).
   */
  private hideTransitionOverlay(): void {
    if (this.transitionDriverObj) {
      this.transitionDriverObj.destroy();
      this.transitionDriverObj = null;
    }
  }

  /**
   * Poll (via requestAnimationFrame) until an element exists, then run the callback.
   * Falls back after `timeout` ms so the tour never gets stuck.
   */
  private waitForElement(selector: string | undefined, cb: () => void, timeout = 5000): void {
    if (!selector) {
      cb();
      return;
    }
    const start = Date.now();
    const check = () => {
      if (document.querySelector(selector)) {
        cb();
        return;
      }
      if (Date.now() - start > timeout) {
        cb();
        return;
      }
      requestAnimationFrame(check);
    };
    check();
  }


  startAcknowledgementTour(): void {
    const acknowledgementStep: DriveStep[] = [
      {
        element: '[ data-tour="acknowledgement-box"]',
        popover: {
          title: this.translate.instant('tour.acknowledgement.title'),
          description: this.translate.instant('tour.acknowledgement.description'),
          side: 'bottom',
          align: 'center'
        }
      }
    ];
    const filteredSteps = acknowledgementStep.filter((step :any) => {
    return !!document.querySelector(step.element);
    })
    this.startAckTour(filteredSteps);
    // Automatically close the tour after 3 seconds
    setTimeout(() => {
      if (this.driverObj.isActive()) {
        this.driverObj.destroy();
      }
    }, 5000);
  }

  /**
   * Highlight a single element without a full tour
   * @param element Element selector or HTMLElement
   * @param config Optional configuration
   */
  highlightElement(element: string | Element, config?: Partial<Config>): void {
    const highlightConfig: Config = {
      showButtons: ['close'],
      steps: [{
        element: element,
        popover: {
          title: 'Feature Highlight',
          description: 'This feature is highlighted for your attention.',
          ...config?.steps?.[0]?.popover
        }
      }],
      ...config
    };

    this.driverObj = driver(highlightConfig);
    this.driverObj.drive();
  }

  /**
   * Move to next step in current tour
   */
  nextStep(): void {
    this.driverObj.moveNext();
  }

  /**
   * Move to previous step in current tour
   */
  previousStep(): void {
    this.driverObj.movePrevious();
  }

  /**
   * Stop and destroy current tour
   */
  stopTour(): void {
    this.driverObj.destroy();
  }

  /**
   * Check if tour is currently active
   */
  isTourActive(): boolean {
    return this.driverObj.isActive();
  }

  /**
   * Get current step index
   */
  getCurrentStepIndex(): number {
    return this.driverObj.getActiveIndex() ?? 0;
  }

  /**
   * Get total number of steps
   */
  getTotalSteps(): number {
    return this.driverObj.getConfig().steps?.length ?? 0;
  }
}